class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get headerNavigation() {
    return this.querySelector('.header__bottom');
  }

  get navigationToggleButton() {
    return this.querySelector('.toggle-navigation-button');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }
    // MAHA: recalcula una vez que las fuentes web reales ya cargaron -- si la
    // primera medicion (delayUntilInteraction, abajo) llega a correr antes de
    // document.fonts.ready, el texto del menu pudo medirse con la fuente de
    // respaldo (ancho/alto distinto), dejando --header-navigation-height
    // corto. Con las fuentes ya listas, esta vuelve a medir con el layout
    // final -- misma fuente de verdad (setHeight), sin timers nuevos.
    if (document.fonts) {
      document.fonts.ready.then(this.setHeight.bind(this));
    }

    // window.addEventListener('resize', this.setMenuHeight.bind(this));

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.handleSectionReload.bind(this));
      section.addEventListener('shopify:section:unload', this.handleSectionReload.bind(this));
      section.addEventListener('shopify:section:reorder', this.handleSectionReload.bind(this));
    }
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  // MAHA: shopify:section:load/unload/reorder disparan en cada guardado del
  // editor sobre esta sección. init() (arriba) creaba en cada uno un
  // FoxTheme.delayUntilInteraction NUEVO -- un timer propio de 5s + su propio
  // set de listeners de interacción -- sin cancelar los anteriores. En una
  // sesión de edición con varios guardados se acumulaban varios timers
  // vivos a la vez, cada uno remidiendo en un momento distinto y arbitrario
  // (hasta 5s después del guardado si el mouse no toca el iframe de
  // preview). El editor ya entrega el HTML de la sección actualizado antes
  // de disparar estos eventos, así que no hace falta diferir mas: se mide
  // de una vez (setHeight ya corre en su propio requestAnimationFrame).
  handleSectionReload() {
    this.setHeight();

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  calculateHeaderGroupHeight() {
    const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeight = 0;

    headerGroups.forEach((section) => {
      totalHeight += section.offsetHeight;
    });

    document.documentElement.style.setProperty('--header-group-height', `${totalHeight}px`);

    return totalHeight;
  }

  setHeight() {
    requestAnimationFrame(() => {
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);
      // MAHA: scrollHeight en vez de offsetHeight. offsetHeight refleja la caja YA
      // recortada por el propio max-height cuando el menu esta colapsado (custom.css,
      // fix "elimina el hueco blanco al colapsar"), asi que si el ResizeObserver mide
      // justo mientras esta colapsado, --header-navigation-height se queda envenenado
      // con ~0px y el menu ya no vuelve a abrir (max-height:0 sigue aplicando aunque se
      // quite la clase is-hide). scrollHeight refleja el alto real del contenido sin que
      // el propio max-height/overflow lo afecte, en cualquier estado.
      const offsetNavigationHeight = Math.round(this.headerNavigation.scrollHeight);

      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
      document.documentElement.style.setProperty('--header-navigation-height', `${offsetNavigationHeight - 1}px`);

      this.calculateHeaderGroupHeight();
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    // Group CSS classes in one object for easier management
    this.classes = {
      pinned: 'header-pinned',
      headerScrolled: 'header-scrolled',
      show: 'is-show',
      hide: 'is-hide',
      headerSticky: 'header-sticky',
      isHideNav: 'is-hide-nav',
    };

    // Track navigation toggle state
    this.navigationManuallyToggled = false;

    // Initialize scroll tracking variables
    this.currentScrollTop = 0;
    this.lastScrollPos = 0;

    // Add scroll threshold to prevent quick unpinning
    this.scrollThreshold = 200; // Minimum scroll amount before unpinning
    this.scrollDirection = 'none';
    this.scrollDistance = 0;

    // MAHA (2026-08-25, reports/menu-un-renglon-20260825) -- banda muerta
    // (histeresis) para el colapso de la fila de categorias. Antes,
    // colapsaba/expandia en el MISMO umbral (headerBoundsTop, ~107-139px
    // segun ancho) -- cualquier scroll que oscilara alrededor de ese punto
    // (un solo tick de rueda ya mueve 100-120px) alternaba is-hide dentro y
    // fuera de ese margen estrecho, lo que el dueño describio como "se
    // chisquea". Colapsa en 200px, expande en 50px: 150px de banda, mas
    // ancha que un tick de rueda normal. 200 queda por debajo del alto del
    // hero medido (493-560px, reports/menu-un-renglon-20260825/
    // b3-medir-hero.js) -- colapsa mientras el usuario aun esta dentro del
    // hero, no despues. Estado propio (_navCollapsed), separado del que ya
    // usa el pin (isScrolledPastHeader) -- ese sigue igual, sin tocar.
    this._navCollapsed = false;
    this.navCollapseThreshold = 200;
    this.navExpandThreshold = 50;

    // MAHA -- sin animacion mientras el usuario esta deslizando (ver
    // updateNavCollapse). El atasco medido (reports/cabecera-fondo-
    // carrera-20260825, 389-638ms en 2 de 4 corridas) es la transicion de
    // max-height congelandose a medio camino porque no es animable por
    // compositor y el hilo principal esta ocupado -- sin transicion activa
    // durante el scroll no hay estado a medias donde atorarse.
    this._scrollEndTimer = null;
  }

  // Getters for easier property access
  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  get collapseOnScroll() {
    return this.dataset.collapseOnScroll === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    // Store initial scroll position
    this.firstScrollTop = window.scrollY;

    // Cache header dimensions for performance optimization
    this.headerBounds = this.headerSection.getBoundingClientRect();

    // Initialize sticky header
    this.initStickyHeader();

    // Register toggle button event if needed
    if (this.collapseOnScroll && this.navigationToggleButton) {
      this.navigationToggleButton.addEventListener('click', this.handleNavigationToggle.bind(this));
    }
  }

  // Initialize sticky header behavior
  initStickyHeader() {
    this.headerSection.classList.add(this.classes.headerSticky);
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
  }

  // Handle navigation toggle button click
  handleNavigationToggle(event) {
    event.preventDefault();

    this.navigationManuallyToggled = true;
    const isHidden = this.headerNavigation.classList.contains(this.classes.hide);

    // Toggle visibility state
    setTimeout(
      () => {
        document.body.classList.toggle(this.classes.isHideNav, !isHidden);
      },
      isHidden ? 0 : 250
    );
    this.headerNavigation.classList.toggle(this.classes.hide, !isHidden);
    this.headerNavigation.classList.toggle(this.classes.show, isHidden);
    this.navigationToggleButton.setAttribute('aria-expanded', isHidden);

    // Reset toggle state after delay
    setTimeout(() => {
      this.navigationManuallyToggled = false;
    }, 1000);
  }

  // Handle scroll events
  // MAHA (2026-08-25): coalescencia de rAF + lectura de scrollTop en vivo (antes: un rAF por evento 'scroll' sin descartar el pendiente, con scrollTop congelado en el evento que lo programo).
  // Motivo: con scroll rapido y cambio de sentido se encolaban varios callbacks que corrian con datos viejos, alternando is-hide/is-show fuera de orden -- la carrera medida en reports/menu-se-traba-20260825.
  // El Hyper original (assets/header.js) tiene el mismo defecto sin corregir -- si una actualizacion del tema lo reemplaza, revisar que esta guarda no se pierda.
  handleScroll() {
    this.updateScrollMetrics(window.scrollY);

    // MAHA -- marca "deslizando activamente" ANTES del rAF, en el primer
    // evento crudo de la rafaga, no un frame despues: custom.css apaga la
    // transicion de .header__bottom mientras esta clase esta puesta
    // (body.is-scrolling-header). Se quita 150ms despues del ULTIMO evento
    // de scroll (debounce, se reinicia en cada evento) -- ese hueco cubre
    // tanto la rafaga de eventos como el frame donde updateNavCollapse
    // podria cambiar de estado, para que ese cambio en concreto tambien
    // caiga sin transicion.
    document.body.classList.add('is-scrolling-header');
    clearTimeout(this._scrollEndTimer);
    this._scrollEndTimer = setTimeout(() => {
      document.body.classList.remove('is-scrolling-header');
    }, 150);

    if (this._scrollRafPending) return;
    this._scrollRafPending = true;

    requestAnimationFrame(() => {
      this._scrollRafPending = false;

      const scrollTop = window.scrollY;
      const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
      const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;
      const isScrolledPastHeader = scrollTop > headerBoundsTop;

      // Handle different scroll positions
      if (isScrolledPastHeader) {
        this.handleScrolledPastHeader(scrollTop, headerBoundsBottom);
      } else {
        this.handleScrolledBeforeHeader();
      }
      // MAHA -- colapso de la fila de categorias con banda muerta propia,
      // desacoplado de isScrolledPastHeader (ver constructor y el metodo
      // updateNavCollapse abajo).
      this.updateNavCollapse(scrollTop);

      this.currentScrollTop = scrollTop;
    });
  }

  // MAHA (2026-08-25) -- colapso de la fila de categorias con histeresis:
  // colapsa al pasar navCollapseThreshold (200), expande solo al bajar de
  // navExpandThreshold (50). Antes ambos vivian dentro de
  // handleScrolledPastHeader/handleScrolledBeforeHeader, con el MISMO
  // umbral (headerBoundsTop) para colapsar y expandir -- cualquier
  // oscilacion alrededor de ese punico punto alternaba is-hide de un lado a
  // otro. _navCollapsed es estado propio, independiente del pin
  // (isAlwaysSticky/scrollDirection, sin tocar arriba).
  updateNavCollapse(scrollTop) {
    if (!this.collapseOnScroll) return;

    if (!this._navCollapsed && scrollTop > this.navCollapseThreshold) {
      this._navCollapsed = true;
    } else if (this._navCollapsed && scrollTop < this.navExpandThreshold) {
      this._navCollapsed = false;
    }

    if (this.navigationManuallyToggled) return;

    if (this._navCollapsed) {
      if (!this.headerNavigation.classList.contains(this.classes.show)) {
        this.headerNavigation.classList.add(this.classes.hide);
        document.body.classList.add(this.classes.isHideNav);
      }
    } else {
      document.body.classList.remove(this.classes.isHideNav);
      this.headerNavigation.classList.remove(this.classes.hide);
      this.headerNavigation.classList.remove(this.classes.show);
      this.navigationToggleButton.setAttribute('aria-expanded', false);
    }
  }

  // Update scroll direction and accumulated distance
  updateScrollMetrics(scrollTop) {
    // Determine scroll direction
    const newDirection = scrollTop > this.currentScrollTop ? 'down' : 'up';

    // Reset accumulated distance when direction changes
    if (newDirection !== this.scrollDirection) {
      this.scrollDistance = 0;
      this.scrollDirection = newDirection;
    } else {
      // Accumulate scroll distance in the same direction
      this.scrollDistance += Math.abs(scrollTop - this.currentScrollTop);
    }
  }

  // Handle scroll position after passing the header
  handleScrolledPastHeader(scrollTop, headerBoundsBottom) {
    this.headerSection.classList.add(this.classes.headerScrolled);

    // Handle collapse on scroll behavior
    // MAHA: el colapso/expansion en si (is-hide, is-hide-nav) se movio a
    // updateNavCollapse (histeresis propia) -- aqui solo queda revelar el
    // boton de alternar manualmente, que no necesita banda muerta.
    if (this.collapseOnScroll) {
      this.navigationToggleButton.classList.add(this.classes.show);
    }

    // Handle sticky behavior based on configuration
    if (this.isAlwaysSticky) {
      document.body.classList.add(this.classes.pinned);
    } else {
      // Improved sticky on scroll up logic
      const isScrollingUp = this.scrollDirection === 'up';
      const isNearHeader = scrollTop < headerBoundsBottom + 100;
      const hasScrolledEnough = this.scrollDistance >= this.scrollThreshold;

      if (isScrollingUp || isNearHeader) {
        document.body.classList.add(this.classes.pinned);
      } else if (!this.navigationManuallyToggled && hasScrolledEnough) {
        // Only unpin when scrolled down enough and not manually toggled
        document.body.classList.remove(this.classes.pinned);
      }
    }
  }

  // Handle scroll position before the header
  handleScrolledBeforeHeader() {
    this.headerSection.classList.remove(this.classes.headerScrolled);

    // MAHA: is-hide/is-hide-nav se movieron a updateNavCollapse -- aqui
    // solo queda ocultar el boton de alternar manualmente.
    if (this.collapseOnScroll) {
      this.navigationToggleButton.classList.remove(this.classes.show);
    }

    if (this.isAlwaysSticky) {
      document.body.classList.remove(this.classes.pinned);
    }
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    // Initialize properties
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    // Reference to first and last child elements
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;

    // Initial state based on attributes
    this._open = this.hasAttribute('open');

    // MAHA: el nombre de la categoria es un <a href> real (navega de forma nativa,
    // sin JS) y la flecha es un <button type="button"> independiente
    // (.menu__item-toggle) que solo expande/contrae. El listener vive en el boton,
    // no en todo el summary, para no interceptar el click del enlace.
    this.toggleButton = this.summaryElement.querySelector('button.menu__item-toggle');
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', this.handleToggleClick.bind(this));
    } else {
      // Fallback defensivo por si algun markup todavia no trae el boton separado.
      this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));
    }

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    // MAHA (2026-08-25): cierre por scroll -- cintillo+header+categorias
    // ahora quedan pegados (position:sticky). Antes, un desplegable abierto
    // desaparecia de la vista solo porque la fila entera se iba con el
    // scroll; nadie escribio codigo para cerrarlo porque no hacia falta.
    // Ahora la fila no se va, asi que nada lo cierra por su cuenta. Mismo
    // patron que los otros 3 handlers de arriba: se agrega/quita en
    // transition(), no vive todo el tiempo pegado al documento.
    this.handleScrollClose = this.handleScrollClose.bind(this);

    // Setup hover detection with debouncing
    this.hoverEnterTimer = null;
    this.hoverLeaveTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    // this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    // this.addEventListener('mouseleave', this.detectHoverListener.bind(this));

    this.addEventListener('mouseenter', (event) => {
      if (this.hoverLeaveTimer) {
        clearTimeout(this.hoverLeaveTimer);
        this.hoverLeaveTimer = null;
      }
      this.hoverEnterTimer = setTimeout(() => {
        this.detectHover({ type: 'mouseenter' });
      }, 100);
    });

    this.addEventListener('mouseleave', (event) => {
      if (this.hoverEnterTimer) {
        clearTimeout(this.hoverEnterTimer);
        this.hoverEnterTimer = null;
      }

      this.hoverLeaveTimer = setTimeout(() => {
        this.detectHover({ type: 'mouseleave' });
      }, 150);
    });
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      // Update the internal state
      this._open = value;

      // MAHA: mantener aria-expanded en el boton de flecha independiente,
      // no solo en el <details>/<summary>.
      if (this.toggleButton) {
        this.toggleButton.setAttribute('aria-expanded', value ? 'true' : 'false');
      }

      // Perform actions based on whether the element is connected to the DOM
      if (this.isConnected) {
        // If connected, perform a transition
        this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    // For touch devices, always use click events
    if (FoxTheme.config.isTouch) {
      return this.getAttribute('trigger') || 'click'
    }

    // For non-touch devices, check for custom trigger attribute
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleToggleClick(event) {
    // MAHA: unico proposito de este boton es expandir/contraer. Nunca navega,
    // por eso preventDefault aqui es seguro y necesario (evita que el toggle
    // nativo del <summary> vuelva a alternar el estado por su cuenta).
    event.preventDefault();
    this.open = !this.open;
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    // event.preventDefault();

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      // Otherwise, toggle the 'open' state
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      // Increment the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) + 1);

      // Add class to body and set attributes
      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => {
        this.contentElement.setAttribute('open', '');
      }, 100);

      // Add event listeners
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);
      // capture:true para recibir tambien el scroll de un submenu largo
      // (contentElement, overflow propio) y poder distinguirlo del scroll
      // de pagina en el propio handler -- ver handleScrollClose.
      this.scrollYAtOpen = window.scrollY;
      document.addEventListener('scroll', this.handleScrollClose, { capture: true, passive: true });

      // Perform the transition in animation
      await this.showWithTransition();

      const MenuProductList = this.querySelector('menu-product-list');
      if (MenuProductList && typeof MenuProductList === 'object') {
        MenuProductList.calcNavButtonsPosition();
      }

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();

      // Wait for the after show event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      // Decrement the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) - 1);

      // Toggle class on body based on lock count
      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0);

      // Remove attributes and event listeners
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('scroll', this.handleScrollClose, { capture: true });
      if (this._scrollCloseRafId) {
        cancelAnimationFrame(this._scrollCloseRafId);
        this._scrollCloseRafId = null;
      }

      // Perform the transition out animation
      await this.hideWithTransition();

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute('open');
      }

      // Wait for the after hide event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  handleScrollClose(event) {
    // MAHA (2026-08-25): coalescido con requestAnimationFrame -- 'scroll' sin
    // throttle puede disparar decenas de veces por segundo; el trabajo real
    // (comparar target, restar scrollY, cerrar) corre como maximo una vez por
    // cuadro, con el evento MAS RECIENTE, en vez de una vez por cada evento
    // crudo. Guarda por instancia (this._scrollCloseRafId) porque cada
    // desplegable abierto tiene su propio listener/estado.
    this._scrollCloseEvent = event;
    if (this._scrollCloseRafId) return;
    this._scrollCloseRafId = requestAnimationFrame(() => {
      this._scrollCloseRafId = null;
      this.processScrollClose(this._scrollCloseEvent);
    });
  }

  processScrollClose(event) {
    // Un scroll DENTRO del propio desplegable (una lista larga con su propio
    // overflow) dispara su propio evento "scroll" con ese elemento como
    // target -- nunca "document". Eso no cierra nada: es el cliente leyendo
    // la lista, no abandonando la pagina.
    if (event.target !== document && this.contains(event.target)) return;
    if (event.target !== document) return;

    if (Math.abs(window.scrollY - this.scrollYAtOpen) > 8) {
      // Foco de vuelta al disparador (flecha o enlace), nunca dentro del
      // contenido que se acaba de ocultar -- si el foco vive ahi, el
      // teclado queda "atrapado" en un elemento invisible.
      const returnFocusTo = this.toggleButton || this.summaryElement.querySelector('a') || this.summaryElement;
      this.open = false;
      if (this.contains(document.activeElement)) {
        returnFocusTo.focus();
      }
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      this.open = event.type === 'mouseenter';
    }
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    // Perform the animation on the first child of the content element
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

customElements.define('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    // MAHA: el nombre de la categoria es un <a href> real (navega); la flecha
    // es un <button type="button" class="menu-drawer__item-toggle"> aparte
    // que solo expande/contrae. El listener va en el boton, no en todo el
    // summary, para no interceptar el click del enlace.
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', this.onToggleClick.bind(this));
    } else {
      // Fallback defensivo por si algun markup todavia no trae el boton separado.
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    }
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get toggleButton() {
    return this.querySelector('summary button.menu-drawer__item-toggle');
  }

  get closeButton() {
    // Selector especifico (antes era "button" a secas): con el boton de
    // flecha nuevo dentro del summary, un selector generico 'button' ahora
    // encontraria ese boton primero en vez del boton "volver".
    return this.querySelector('.menu-drawer__item-back-link');
  }

  onSummaryClick() {
    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    }, 100);
  }

  onToggleClick(event) {
    // Nunca navega: preventDefault aqui es seguro (el boton no tiene href) y
    // necesario para que el toggle nativo del <summary> no vuelva a alternar
    // el estado por su cuenta ademas de nuestra logica.
    event.preventDefault();

    if (this.hasAttribute('open')) {
      this.onCloseButtonClick();
    } else {
      this.setAttribute('open', '');
      this.toggleButton.setAttribute('aria-expanded', 'true');
      this.onSummaryClick();
    }
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', 'false');
    }

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  get header() {
    return this.closest('header');
  }

  // get menuItems() {
  //   if (!this._menuItems) {
  //     this._menuItems = this.querySelectorAll(
  //       '.menu-drawer__menus:not(.active) > ul > li, .menu-drawer__menus:not(.active) > .menu-drawer__footer'
  //     );
  //   }
  //   return this._menuItems;
  // }

  get requiresBodyAppended() {
    return false;
  }

  prepareToShow() {
    super.prepareToShow();
    setTimeout(() => {
      // this.animateMenuItems();
    }, 300);

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    // MAHA: la medición de arriba corre ANTES de que show() agregue "modal-showing"
    // a <body> (mismo ciclo síncrono, ver ModalComponent.show() en theme.js), y ese
    // overflow:hidden + padding-right (theme.css .modal-showing) hace que <header>
    // reduzca su alto real (medido con Playwright: 176px -> 143px, 390px de ancho)
    // ni bien se aplica la clase. Sin re-medir, #MenuDrawer y su overlay quedan
    // posicionados con el valor viejo (mas alto) durante ~1s, hasta que
    // handleAfterShow() vuelve a medir al terminar la transicion -- en ese hueco de
    // ~33px entre el <header> ya encogido y el panel/overlay todavia sin subir, se
    // alcanza a ver (atenuado por el overlay propio de .header-section, no opaco)
    // el contenido de la pagina de atras. Re-medir en el siguiente frame (ya con
    // "modal-showing" aplicado) cierra ese hueco casi de inmediato en vez de
    // esperar el final de la transicion.
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom)}px`
      );
    });

    document.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
  }

  // animateMenuItems() {
  //   FoxTheme.Motion.animate(
  //     this.menuItems,
  //     {
  //       transform: ['translateX(-20px)', 'translateX(0)'],
  //       opacity: [0, 1],
  //     },
  //     {
  //       duration: 0.6,
  //       easing: [0.075, 0.82, 0.165, 1],
  //       delay: FoxTheme.Motion.stagger(0.1),
  //     }
  //   ).finished.then(() => {
  //     this.menuItems.forEach((item) => item.removeAttribute('style'));
  //   });
  // }

  handleAfterHide() {
    super.handleAfterHide();
    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        menu.onCloseButtonClick();
      });
    });
  }

  // MAHA: prepareToShow() mide this.header.getBoundingClientRect().bottom
  // ANTES de que ModalComponent.handleAfterShow() agregue "modal-show" a
  // <body> (ver theme.js: show() llama prepareToShow() primero, "modal-show"
  // se agrega al final de handleAfterShow(), tras la transicion). Con el
  // topbar/anuncio de MAHA encima del header sticky, agregar overflow:hidden
  // en ese punto hace que el header salte de su posicion normal (top:34, bajo
  // el topbar) a top:0 (theme.css .header-sticky), un salto de layout que
  // prepareToShow() no alcanza a ver. --header-bottom-position quedaba
  // congelado con el valor viejo (mas alto), dejando un hueco sin overlay
  // entre el header ya saltado y el drawer/fondo oscurecido, por donde se
  // veia y se podia interactuar con el contenido de la pagina de atras.
  // Se vuelve a medir aqui, que corre justo despues de que handleAfterShow()
  // agrega "modal-show" (ver arriba), cuando el header ya esta en su
  // posicion final.
  handleAfterShow() {
    super.handleAfterShow();
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;

    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }
}
customElements.define('menu-product-list', MenuProductList);

class MenuSidebar extends HTMLElement {
  constructor() {
    super();
    this.handleSidenavMenuToggle = this.handleSidenavMenuToggle.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
  }

  get summarys() {
    return this.querySelectorAll('summary');
  }

  connectedCallback() {
    this.setInitialMinHeight();

    const firstSummary = this.summarys[0];
    if (firstSummary) {
      this.setActiveItem(firstSummary, false);
    }

    this.summarys.forEach((summary) => {
      summary.addEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.addEventListener('click', (e) => e.preventDefault());
    });

    this.setupIntersectionObserver();
  }

  setInitialMinHeight() {
    const container = this.closest('.mega-menu__wrapper');
    if (container) {
      const parentHeight = container.parentElement.offsetHeight;
      container.style.setProperty('--menu-sidebar-height', `${parentHeight}px`);
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.updateHeight();
        }
      });
    });

    observer.observe(this);
  }

  updateHeight() {
    const activeSummary = this.querySelector('.is-visible');
    if (activeSummary) {
      const container = this.closest('.mega-menu__wrapper');
      const content = activeSummary.nextElementSibling;
      if (container && content) {
        container.style.setProperty('--sidebar-height', `${content.scrollHeight}px`);
      }
    }
  }

  setActiveItem(summaryElem, updateHeight = true) {
    const lastSidenavElem = this.querySelector('.is-visible');
    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
    }

    summaryElem.classList.add('is-visible');

    if (updateHeight) {
      this.updateHeight();
    }
  }

  handleSidenavMenuToggle(evt) {
    const summaryElem = evt.target;
    this.setActiveItem(summaryElem);
  }

  disconnectedCallback() {
    this.summarys.forEach((summary) => {
      summary.removeEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.removeEventListener('click', this.handleClick);
    });
  }
}

customElements.define('menu-sidebar', MenuSidebar);
